/**
 * Generic utilities for array operations, grouping, and batching.
 * Reduces code duplication across services.
 */

/**
 * Groups an array of items by a key extracted from each item.
 * Generic replacement for multiple groupBy* functions.
 *
 * @param items Array of items to group
 * @param keyExtractor Function to extract the grouping key from each item
 * @param sortFn Optional sort function to sort items before grouping
 * @returns Object with keys and arrays of items
 */
export function groupBy<T, K extends string | number>(
  items: T[],
  keyExtractor: (item: T) => K,
  sortFn?: (a: T, b: T) => number
): Record<K, T[]> {
  const sorted = sortFn ? [...items].sort(sortFn) : items;

  return sorted.reduce((acc, item) => {
    const key = keyExtractor(item);
    if (key == null) {
      return acc;
    }

    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);

    return acc;
  }, {} as Record<K, T[]>);
}

/**
 * Standard date-based sort comparator for patches and sync items.
 */
export function sortByDate<T extends { date?: Date }>(a: T, b: T): number {
  return (+a?.date || 0) - (+b?.date || 0);
}

/**
 * Processes items in batches to prevent overwhelming the system.
 *
 * @param items Items to process
 * @param batchSize Number of items per batch
 * @param processor Function to process each batch
 * @returns Results from all batches
 */
export async function processBatched<T, R>(
  items: T[],
  batchSize: number,
  processor: (batch: T[]) => Promise<R[]>
): Promise<R[]> {
  const results: R[] = [];

  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    const batchResults = await processor(batch);
    results.push(...batchResults);
  }

  return results;
}

/**
 * Executes promises in parallel with a concurrency limit.
 *
 * @param items Items to process
 * @param concurrency Maximum number of concurrent operations
 * @param processor Function to process each item
 * @returns Results from all items
 */
export async function parallelLimit<T, R>(
  items: T[],
  concurrency: number,
  processor: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let currentIndex = 0;

  async function worker(): Promise<void> {
    while (currentIndex < items.length) {
      const index = currentIndex++;
      results[index] = await processor(items[index]);
    }
  }

  const workers = Array(Math.min(concurrency, items.length))
    .fill(null)
    .map(() => worker());

  await Promise.all(workers);
  return results;
}

/**
 * Creates a memoized version of an async function.
 * Useful for caching DB connections within a sync cycle.
 *
 * @param fn Function to memoize
 * @param keyFn Function to generate cache key from arguments
 * @returns Memoized function
 */
export function memoizeAsync<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  keyFn: (...args: Parameters<T>) => string
): T {
  const cache = new Map<string, Promise<any>>();

  return (async (...args: Parameters<T>) => {
    const key = keyFn(...args);

    if (cache.has(key)) {
      return cache.get(key);
    }

    const promise = fn(...args);
    cache.set(key, promise);

    try {
      return await promise;
    } catch (error) {
      cache.delete(key);
      throw error;
    }
  }) as T;
}

/**
 * Chunks an array into smaller arrays of specified size.
 *
 * @param array Array to chunk
 * @param size Maximum size of each chunk
 * @returns Array of chunks
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Deduplicates an array by a key extracted from each item.
 *
 * @param items Array of items
 * @param keyExtractor Function to extract unique key
 * @returns Deduplicated array
 */
export function uniqueBy<T>(items: T[], keyExtractor: (item: T) => any): T[] {
  const seen = new Set();
  return items.filter(item => {
    const key = keyExtractor(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

